import { create } from "zustand";
import { apiCall, ApiError } from "@/lib/api";
import type {
  Attempt,
  CoachingResponse,
  LeaderboardResponse,
  ReplayResponse,
  RespondParams,
  RespondResult,
  ResultsResponse,
  SimulationTree,
  StartAttemptResponse,
  TreeNode,
} from "@/types/simulation";

interface AttemptStoreState {
  tree: SimulationTree | null;
  attempt: Attempt | null;
  resumed: boolean;
  loadingTree: boolean;
  startingAttempt: boolean;
  error: string | null;

  loadTree: (simId: string) => Promise<void>;
  startAttempt: (simId: string) => Promise<Attempt>;
  respond: (params: RespondParams) => Promise<RespondResult>;
  advance: (params: { attemptId: string; nodeId: string }) => Promise<{ nextNodeId: string | null; completed: boolean }>;
  timeoutAttempt: (attemptId: string) => Promise<void>;
  saveReflection: (attemptId: string, text: string) => Promise<void>;
  fetchResults: (attemptId: string) => Promise<ResultsResponse>;
  fetchCoaching: (attemptId: string, regenerate?: boolean) => Promise<CoachingResponse>;
  fetchLeaderboard: (simId: string) => Promise<LeaderboardResponse>;
  fetchReplay: (simId: string) => Promise<ReplayResponse>;
  nodeById: (nodeId: string | null) => TreeNode | null;
  reset: () => void;
}

function toMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export const useAttemptStore = create<AttemptStoreState>((set, get) => ({
  tree: null,
  attempt: null,
  resumed: false,
  loadingTree: false,
  startingAttempt: false,
  error: null,

  loadTree: async (simId: string): Promise<void> => {
    // Always refetch. The in-session cache was masking authoring edits:
    // an admin would save a change (e.g. videoHasQuestion = false in the
    // Sim Library) but the same browser tab would still hold the OLD
    // tree on the next attempt — so the runtime saw stale node fields.
    // Refetching costs ~100ms on a normal sim; correctness wins.
    set({ loadingTree: true, error: null });
    try {
      const tree = await apiCall<SimulationTree>(`/sim/${simId}`);
      set({ tree, loadingTree: false });
    } catch (err) {
      set({ error: toMessage(err), loadingTree: false });
    }
  },

  startAttempt: async (simId: string): Promise<Attempt> => {
    set({ startingAttempt: true, error: null });
    try {
      const res = await apiCall<StartAttemptResponse>("/attempt/start", {
        method: "POST",
        body: { simulationId: simId },
      });
      set({ attempt: res.attempt, resumed: res.resumed, startingAttempt: false });
      return res.attempt;
    } catch (err) {
      set({ error: toMessage(err), startingAttempt: false });
      throw err;
    }
  },

  respond: async (params: RespondParams): Promise<RespondResult> => {
    const result = await apiCall<RespondResult>(`/attempt/${params.attemptId}/respond`, {
      method: "POST",
      body: {
        nodeId: params.nodeId,
        choiceId: params.choiceId,
        answerText: params.answerText,
        rejectionCount: params.rejectionCount ?? 0,
        antiCheat: params.antiCheat,
      },
      // OpenText answers are scored by Claude — allow more than the 25s default.
      timeoutMs: 60_000,
    });
    // Rejected answers are not scored/advanced — leave attempt state untouched.
    if (result.rejected) return result;
    // Advance local attempt state to match the server
    set((state) => ({
      attempt:
        state.attempt === null
          ? null
          : {
              ...state.attempt,
              currentNodeId: result.nextNodeId,
              currentScreen: result.completed ? "completed" : "decision",
              status: result.completed ? "completed" : state.attempt.status,
              totalScore: result.totalScore,
              totalXp: result.totalXp,
            },
    }));
    return result;
  },

  advance: async (params: { attemptId: string; nodeId: string }): Promise<{ nextNodeId: string | null; completed: boolean }> => {
    const result = await apiCall<{ nextNodeId: string | null; completed: boolean }>(
      `/attempt/${params.attemptId}/advance`,
      { method: "POST", body: { nodeId: params.nodeId } },
    );
    set((state) => ({
      attempt:
        state.attempt === null
          ? null
          : {
              ...state.attempt,
              currentNodeId: result.nextNodeId,
              currentScreen: result.completed ? "completed" : "decision",
              status: result.completed ? "completed" : state.attempt.status,
            },
    }));
    return result;
  },

  timeoutAttempt: async (attemptId: string): Promise<void> => {
    await apiCall(`/attempt/${attemptId}/timeout`, { method: "POST" });
    // Mirror the server: the attempt is now closed.
    set((state) => ({
      attempt:
        state.attempt === null
          ? null
          : { ...state.attempt, status: "timed_out", currentScreen: "timed_out", currentNodeId: null },
    }));
  },

  saveReflection: async (attemptId: string, text: string): Promise<void> => {
    await apiCall(`/attempt/${attemptId}/reflection`, { method: "POST", body: { reflectionText: text } });
  },

  fetchResults: async (attemptId: string): Promise<ResultsResponse> => {
    // 120s + one silent retry on abort — same shape as the analytics page.
    // The 25s default trips on Azure Functions cold start before the query
    // even runs, surfacing "signal is aborted without reason" to the learner.
    const call = (): Promise<ResultsResponse> => apiCall<ResultsResponse>(`/attempt/${attemptId}/results`, { timeoutMs: 120_000 });
    try {
      return await call();
    } catch (e) {
      const isAbort = e instanceof Error && /abort/i.test(e.message);
      if (!isAbort) throw e;
      await new Promise((r) => setTimeout(r, 1000));
      return await call();
    }
  },

  fetchCoaching: async (attemptId: string, regenerate = false): Promise<CoachingResponse> => {
    return apiCall<CoachingResponse>(`/attempt/${attemptId}/coaching`, {
      method: "POST",
      body: { regenerate },
      // Generating the full debrief can take well over a minute.
      timeoutMs: 150_000,
    });
  },

  fetchLeaderboard: async (simId: string): Promise<LeaderboardResponse> => {
    return apiCall<LeaderboardResponse>(`/sim/${simId}/leaderboard`);
  },

  fetchReplay: async (simId: string): Promise<ReplayResponse> => {
    return apiCall<ReplayResponse>(`/sim/${simId}/replay`);
  },

  nodeById: (nodeId: string | null): TreeNode | null => {
    if (nodeId === null) return null;
    const tree = get().tree;
    if (tree === null) return null;
    return tree.nodes.find((n) => n.id === nodeId) ?? null;
  },

  reset: (): void => set({ tree: null, attempt: null, resumed: false, error: null }),
}));
